import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';

/**
 * Кабінет ДПС → «створити»: КАТАЛОГ типів документів для відправки у ДПС.
 * Бухгалтер шукає потрібний документ у полі пошуку → обирає → потрапляє на
 * окрему сторінку створення саме цього документа. Кожен тип має свою
 * сторінку (податковий розрахунок, сервісний запит, повідомлення тощо).
 * ч.1392 (рішення власника): каталог — список (DataTable), а не плитки; назва документа —
 * посилання (вибір одним клацанням), подвійне клацання по рядку теж відкриває.
 */

interface DocType {
  key: string;
  title: string;
  code: string;        // код форми/родини — для пошуку
  keywords: string;    // додаткові слова для пошуку
  route: string;       // сторінка створення
  group: string;
}

const DOC_TYPES: DocType[] = [
  {
    key: 'j05',
    title: 'Податковий розрахунок сум доходу (ПДФО / ВЗ / ЄСВ)',
    code: 'J0500110',
    keywords: 'об\'єднана звітність єдиний внесок пдфо військовий збір 4дф '
      + 'д1 д5 зарплата найманих працівників',
    route: '/tax-reports-j05/new',
    group: 'Звітність',
  },
  {
    key: 'vat-decl',
    title: 'Податкова декларація з ПДВ',
    code: 'J0200126',
    keywords: 'пдв декларація податок на додану вартість д1 додаток '
      + 'зобов\'язання кредит',
    route: '/taxation/vat-declarations/new',
    group: 'Звітність',
  },
  {
    key: 'profit-tax-decl',
    title: 'Податкова декларація з податку на прибуток підприємств (з додатком РІ і фінансовою звітністю)',
    code: 'J0100130',
    keywords: 'податок на прибуток прибуток декларація квартальна річна різниці додаток рі '
      + 'фінансовий результат 40 млн загальна система збиток минулих років',
    route: '/taxation/profit-tax-declarations/new',
    group: 'Звітність',
  },
  {
    key: 'financial-statements',
    title: 'Фінансова звітність: Баланс і Звіт про фінансові результати (№1-мс/№2-мс, №1-м/№2-м, №1/№2)',
    code: 'J0901205 / J0901107 / J0900108 + J0900207',
    keywords: 'фінансова звітність баланс звіт про фінансові результати фінзвіт мікропідприємство '
      + 'мале підприємство форма 1 форма 2 1-м 2-м 1-мс 2-мс',
    route: '/reports/financial/new',
    group: 'Звітність',
  },
  {
    key: 'single-tax-decl',
    title: 'Податкова декларація платника єдиного податку (ФОП 1–3 групи, юрособа 3 групи)',
    code: 'F0103309 / F0103407 / J0103509',
    keywords: 'єдиний податок єдинник спрощена система фоп третя група друга '
      + 'перша група юрособа декларація єсв за себе додаток 1 військовий збір',
    route: '/taxation/single-tax-declarations/new',
    group: 'Звітність',
  },
  {
    key: 'disability-contribution',
    title: 'Звіт про нарахування, обчислення та сплату внеску на підтримку працевлаштування осіб з інвалідністю',
    code: 'J0500701 / F0500701',
    keywords: 'інвалідність особи з інвалідністю норматив робочих місць внесок 10-поі '
      + 'працевлаштування квартальний звіт середньооблікова кількість чотири відсотки',
    route: '/taxation/disability-contribution-reports/new',
    group: 'Звітність',
  },
  {
    key: 'erpn-appeal',
    title: 'Пояснення та копії документів до ПН/РК, реєстрацію якої зупинено (оскарження блокування, скарга)',
    code: 'J1312603 / J1307801 / J1313205',
    keywords: 'заблокована податкова накладна зупинено реєстрацію смкор пояснення копії '
      + 'документів бухгалтерська довідка скарга комісія розблокувати пн рк таблиця даних',
    route: '/erpn-appeals/new',
    group: 'ПДВ',
  },
  {
    key: 'dps-request',
    title: 'Сервісний запит до ДПС (витяг, стан розрахунків)',
    code: 'J1300208',
    keywords: 'запит витяг стан розрахунків з бюджетом пеня платежі '
      + 'інформаційна довідка',
    route: '/dps-cabinet/requests/new',
    group: 'Запити',
  },
  {
    key: 'hire-notif',
    title: 'Повідомлення про прийняття працівника на роботу',
    code: 'J3001005',
    keywords: 'повідомлення прийняття на роботу працівник кадри '
      + 'до початку роботи',
    route: '/hire-notifications/new',
    group: 'Кадри',
  },
];

export default function DpsCabinetNewPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return DOC_TYPES;
    return DOC_TYPES.filter((d) =>
      `${d.title} ${d.code} ${d.keywords} ${d.group}`.toLowerCase().includes(q));
  }, [search]);

  const columns = useMemo<ColumnDef<DocType>[]>(() => [
    { key: 'group', header: 'Розділ', width: 130, cell: (d) => d.group, sortValue: (d) => d.group },
    {
      key: 'title', header: 'Документ', sortValue: (d) => d.title,
      cell: (d) => <Link to={d.route}>{d.title}</Link>,
    },
    {
      key: 'code', header: 'Код форми', width: 350, sortValue: (d) => d.code,
      cell: (d) => <span style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{d.code}</span>,
    },
  ], []);

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Створити документ для ДПС</h2>
        <button style={{ marginLeft: 'auto' }}
          onClick={() => navigate('/dps-cabinet')}>до Кабінету</button>
      </div>
      <p style={{ color: 'var(--muted)', fontSize: '0.85rem', marginTop: 0 }}>
        Оберіть тип документа для подання у ДПС: клацніть назву. Почніть вводити назву або код
        форми — список відфільтрується.
      </p>

      <input
        autoFocus
        value={search}
        placeholder="напр. «податковий розрахунок», «витяг», «прийняття на роботу»"
        onChange={(e) => setSearch(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && filtered.length === 1) {
            navigate(filtered[0]!.route);
          }
        }}
        style={{ width: '100%', maxWidth: 620, padding: '0.5rem 0.7rem',
          fontSize: '0.95rem', marginBottom: '1rem' }}
      />

      <DataTable
        rows={filtered}
        columns={columns}
        rowKey={(d) => d.key}
        emptyText="Нічого не знайдено."
        onRowDoubleClick={(d) => navigate(d.route)}
        storageKey="dps-cabinet-new"
      />
    </div>
  );
}
